import { TIdVerificationPhone } from '@helpers/FundingRequirements/fundingRequirements.types';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { fetchPostIdCheck } from '@iwoca/lapi-client/edge_de';
import { getWindowLocation } from '@iwRouter/LocationHelper';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStateKey } from '../../../../../../../../../../iwQueryClient/useStateKey';
import { useFundingRequirementsParams } from '../../../../hooks/useFundingRequirementsParams';

export type TSendIdCheckSmsParams = {
    personUid: string;
    phone: TIdVerificationPhone;
};

export function useSendIdCheckSms({
    onSent
}: {
    onSent: (params: TSendIdCheckSmsParams) => void;
}) {
    const stateKey = useStateKey();
    const { offerId } = useFundingRequirementsParams();
    const queryClient = useQueryClient();

    const { mutate, isLoading, isError, variables } = useMutation({
        mutationFn: async ({ personUid, phone }: TSendIdCheckSmsParams) => {
            const response = await fetchPostIdCheck({
                stateKey,
                body: {
                    data: {
                        person_uid: personUid,
                        phone_uid: phone.uid,
                        offer_id: offerId,
                        // The ID check provider sends the customer back here once they finish.
                        redirect_url: getWindowLocation().href
                    }
                }
            });

            if (!response.ok) {
                throw new Error(
                    `useSendIdCheckSms: failed to send the ID check SMS: ${response.status}`
                );
            }
        },
        onSuccess: (_, params) => {
            // Before the refetch, so the sent view has the number as soon as it shows.
            onSent(params);
            return queryClient.invalidateQueries({
                queryKey: ['fetchGetFundingRequirements']
            });
        },
        onError: (error: Error) =>
            logError(error.message, { tags: TAGS.DE_LENDING })
    });

    return {
        send: (params: TSendIdCheckSmsParams) => mutate(params),
        retry: () => {
            if (variables) mutate(variables);
        },
        sendingPersonUid: isLoading ? variables?.personUid : undefined,
        isSending: isLoading,
        isSendError: isError
    };
}
